import { Upload } from '@runelab/odin-common'
import { saveAs } from 'file-saver'
import { SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from '@/components/ui/attachment'
import { getFileIcon } from 'utils/chat-attachments'
import { ChatAttachment } from 'types/chat-attachment'
import { getSignedUrl } from 'api/uploads'

// Single normalized model rendered by AttachmentMessage, fed from either an operator `Upload`
// or a bot `ChatAttachment`.
export type MessageAttachment = {
  name: string
  url?: string
  mimeType?: string
}

// Il nome finisce nell'`alt`, quindi non puo ripiegare sull'url: si vedrebbe scritto nella
// bolla finche l'immagine non ha caricato. Dall'url si prende al massimo il basename, che e
// un nome sensato e conserva l'estensione (serve a riconoscere le immagini senza mimeType).
const nameFromUrl = (url?: string): string => {
  const basename = url?.split(/[?#]/)[0].split('/').pop()
  return basename || 'File'
}

export const fromUpload = (upload: Upload): MessageAttachment => ({
  name: upload.additionalInfo?.fullName ?? upload.originalName ?? nameFromUrl(upload.full),
  url: upload.full,
})

export const fromChatAttachment = (attachment: ChatAttachment): MessageAttachment => ({
  name: attachment.name,
  url: attachment.uploadedFile?.full,
  mimeType: attachment.mimeType,
})

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']

const isImageAttachment = (attachment: MessageAttachment): boolean => {
  if (attachment.mimeType) return attachment.mimeType.startsWith('image/')
  const ext = attachment.name.split('.').pop()?.toLowerCase() || ''
  return IMAGE_EXTENSIONS.includes(ext)
}

export type AttachmentMessageProps = {
  attachment: MessageAttachment
  align?: 'left' | 'right'
}

export const AttachmentMessage = ({ attachment, align = 'left' }: AttachmentMessageProps) => {
  const { t } = useTranslation()
  const [imageError, setImageError] = useState(false)
  // L'url memorizzato puo essere scaduto: il browser DEVE fallire prima che possiamo
  // rifirmarlo, e nel frattempo mostrerebbe l'alt. Si tiene nascosta fino al load.
  const [imageLoaded, setImageLoaded] = useState(false)

  const isImage = isImageAttachment(attachment) && !imageError

  // Un url firmato scade dopo 2 ore: prima di ripiegare sulla card-file si riprova una volta
  // con una firma fresca.
  const onImageError = async (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget

    if (img.dataset.signedRetry) {
      setImageError(true)
      return
    }

    img.dataset.signedRetry = '1'

    const fresh = await getSignedUrl(img.src)

    if (fresh && fresh !== img.src) {
      img.src = fresh
      return
    }

    setImageError(true)
  }
  const extension = attachment.name.split('.').pop()?.toUpperCase()

  const download = async () => {
    try {
      if (!attachment.url) return
      // l'url memorizzato e nudo: da solo vale 403 su un file privato
      const response = await fetch(await getSignedUrl(attachment.url))
      saveAs(await response.blob(), attachment.name)
    } catch (e) {
      console.error({ e })
    }
  }

  const card = isImage ? (
    <Attachment orientation="vertical" className="ring-inset">
      <AttachmentMedia variant="image" className={clsx(!imageLoaded && 'animate-pulse bg-muted')}>
        <img
          src={attachment.url}
          alt={attachment.name}
          onLoad={() => setImageLoaded(true)}
          onError={onImageError}
          className={clsx('transition-opacity duration-200', imageLoaded ? 'opacity-100' : 'opacity-0')}
        />
      </AttachmentMedia>
      <AttachmentTrigger aria-label={attachment.name} onClick={() => download().catch(console.error)} />
    </Attachment>
  ) : (
    <Attachment className="max-w-[280px] ring-inset">
      <AttachmentMedia variant="icon">
        <OdinIcon icon={['fas', getFileIcon(attachment.name)]} />
      </AttachmentMedia>
      <AttachmentContent>
        <AttachmentTitle>{attachment.name}</AttachmentTitle>
        {extension && <AttachmentDescription>{extension}</AttachmentDescription>}
      </AttachmentContent>
      <AttachmentActions>
        <AttachmentAction
          type="button"
          variant="secondary"
          size="icon-sm"
          title={t('download')}
          aria-label={`${t('download')} ${attachment.name}`}
          onClick={() => download().catch(console.error)}
        >
          <OdinIcon aria-hidden icon={['far', 'arrow-down-to-line']} />
        </AttachmentAction>
      </AttachmentActions>
    </Attachment>
  )

  return <div className={clsx('flex', align === 'left' ? 'justify-start' : 'justify-end')}>{card}</div>
}
